/* eslint-disable no-param-reassign */
import { create } from 'zustand';

// eslint-disable-next-line import/no-unresolved
import produce from 'immer';

import { getBreakpointsConfig, DESKTOP } from 'hooks/useBreakpoint';

const initialBreakpoint = getBreakpointsConfig(window.innerWidth);
const isDesktop = initialBreakpoint === DESKTOP;

export const useChatStore = create((set, get) => ({
  lastUpdated: null,
  connectionKey: null,
  hasNewMessages: false,
  setConnectionKey: (value) => set(() => ({ connectionKey: value })),
  setLastUpdated: (value) => set(() => ({ lastUpdated: value })),
  isChatExpanded: isDesktop,
  setIsChatExpanded: (value) => set(() => ({ isChatExpanded: value })),
  currentRoom: null,
  setCurrentRoom: (room) => set(() => ({ currentRoom: room })),
  chatrooms: [],
  addNewChatroom: (room) =>
    set((state) => ({
      chatrooms: [
        ...state.chatrooms,
        {
          ...room,
          pinnedMessages: room.messages
            ? room.messages.filter((msg) => msg.pinned)
            : [],
        },
      ],
    })),
  bingoRoomsIsVisible: false,
  setBingoRoomsVisibility: (val) => set(() => ({ bingoRoomsIsVisible: val })),
  addNewMessage: (index, message) =>
    set(
      produce((draft) => {
        const existingMsgIndex = draft.chatrooms[index].messages.findIndex(
          (msg) => msg.chatMessageId === message.chatMessageId
        );

        if (existingMsgIndex >= 0) {
          draft.chatrooms[index].messages[existingMsgIndex] = message;
        } else {
          draft.chatrooms[index].messages.push(message);
        }

        const pinnedIndex = draft.chatrooms[index].pinnedMessages?.findIndex(
          (msg) => msg.chatMessageId === message.chatMessageId
        );

        if (message.pinned && pinnedIndex === -1) {
          if (!draft.chatrooms[index].pinnedMessages) {
            draft.chatrooms[index].pinnedMessages = [];
          }
          draft.chatrooms[index].pinnedMessages.push(message);
        } else if (!message.pinned && pinnedIndex !== -1) {
          draft.chatrooms[index].pinnedMessages.splice(pinnedIndex, 1);
        }

        if (!get().isChatExpanded) {
          draft.hasNewMessages = true;
        }
      })
    ),
  updateMessage: (index, message) =>
    set(
      produce((draft) => {
        const messageIndex = draft.chatrooms[index].messages.findIndex(
          (msg) => msg.chatMessageId === message.chatMessageId
        );

        const pinnedIndex = draft.chatrooms[index].pinnedMessages?.findIndex(
          (msg) => msg.chatMessageId === message.chatMessageId
        );

        if (message.pinned) {
          if (messageIndex >= 0) {
            draft.chatrooms[index].messages[messageIndex].pinned = true;

            Object.keys(message).forEach((key) => {
              draft.chatrooms[index].messages[messageIndex][key] = message[key];
            });
          }

          if (pinnedIndex === -1) {
            if (!draft.chatrooms[index].pinnedMessages) {
              draft.chatrooms[index].pinnedMessages = [];
            }
            draft.chatrooms[index].pinnedMessages.push(message);
          }
        } else {
          if (messageIndex >= 0) {
            draft.chatrooms[index].messages[messageIndex].pinned = false;

            Object.keys(message).forEach((key) => {
              draft.chatrooms[index].messages[messageIndex][key] = message[key];
            });
          }

          if (pinnedIndex !== -1) {
            draft.chatrooms[index].pinnedMessages.splice(pinnedIndex, 1);
          }
        }
      })
    ),
  updateUnreadCount: (index) =>
    set(
      produce((draft) => {
        draft.chatrooms[index].unreadCount += 1;
      })
    ),
  resetUnreadCount: (index) =>
    set(
      produce((draft) => {
        draft.chatrooms[index].unreadCount = 0;
      })
    ),
  updateParticipants: (index, participants) =>
    set(
      produce((draft) => {
        draft.chatrooms[index].chatParticipants = participants;
      })
    ),
  updateParticipantCount: (index, participantCount) =>
    set(
      produce((draft) => {
        draft.chatrooms[index].chatParticipantCount = participantCount;
      })
    ),
  removeChatroom: (id) =>
    set((state) => ({
      chatrooms: state.chatrooms.filter((r) => r.roomId !== id),
    })),
  updateChatrooms: (newList) =>
    set(() => ({
      chatrooms: newList,
    })),
  activeChatRequest: [],
  addActiveChatRequest: (req) =>
    set((state) => ({
      activeChatRequest: [...state.activeChatRequest, req],
    })),
  removeActiveChatRequest: (oldReq) =>
    set((state) => ({
      activeChatRequest: state.activeChatRequest.filter(
        (req) => req !== oldReq
      ),
    })),
  privateChatUserBlockModalMsg: [],
  addPrivateChatUserBlockModalMsg: (msg) =>
    set((state) => ({
      privateChatUserBlockModalMsg: [
        ...state.privateChatUserBlockModalMsg,
        msg,
      ],
    })),
  removePrivateChatUserBlockModalMsg: (msg) =>
    set((state) => ({
      privateChatUserBlockModalMsg: state.privateChatUserBlockModalMsg.filter(
        (m) => m !== msg
      ),
    })),
}));

export default useChatStore;
